import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Rocket, Globe, Zap, Webhook, Trash2, Plus, ExternalLink, Copy, BookOpen, Lock,
    TrendingUp,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";

const ENVS = [
    { key: "dev", label: "Development", color: "text-amber-400", bg: "border-amber-400/30 bg-amber-400/5" },
    { key: "stage", label: "Staging", color: "text-cyan-400", bg: "border-cyan-400/30 bg-cyan-400/5" },
    { key: "prod", label: "Production", color: "text-emerald-400", bg: "border-emerald-400/30 bg-emerald-400/5" },
];

export default function FlowPublishTab({ flow, reload }) {
    const [pubs, setPubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // env being edited

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/flows/${flow.id}/publications`);
            setPubs(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [flow.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const byEnv = (env) => pubs.find((p) => p.environment === env);

    const unpublish = async (env) => {
        if (!window.confirm(`Unpublish ${env}? The public endpoint will start returning 404 immediately.`)) return;
        try {
            await api.delete(`/admin/flows/${flow.id}/publications/${env}`);
            toast.success(`Unpublished ${env}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 space-y-5" data-testid="flow-publish-tab">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Rocket className="w-4 h-4 text-[#7C3AED]" />
                        <h2 className="font-display text-lg tracking-tight">API publishing</h2>
                    </div>
                    <div className="text-xs text-white/50 mt-0.5">
                        Expose this flow as a public API endpoint per environment. Each env pins a specific version.
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                {ENVS.map((env) => {
                    const pub = byEnv(env.key);
                    return (
                        <PublicationCard
                            key={env.key}
                            env={env}
                            pub={pub}
                            flow={flow}
                            onPublish={() => setEditing(env.key)}
                            onUnpublish={() => unpublish(env.key)}
                            onEdit={() => setEditing(env.key)}
                        />
                    );
                })}
            </div>

            {/* Per-env docs */}
            {pubs.length > 0 && (
                <PublishedDocs pubs={pubs} flow={flow} />
            )}

            {editing && (
                <PublishModal
                    flow={flow}
                    environment={editing}
                    existing={byEnv(editing)}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); reload && reload(); }}
                />
            )}
        </div>
    );
}

function PublicationCard({ env, pub, flow, onPublish, onUnpublish, onEdit }) {
    const isPublished = !!pub;
    const [spark, setSpark] = useState(null);

    useEffect(() => {
        if (!isPublished || !flow?.id) return;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get(
                    `/admin/billing/usage/sparkline?flow_id=${flow.id}&hours=24&bucket=hour`,
                );
                if (!cancelled) setSpark(data);
            } catch (e) {
                if (!cancelled) setSpark({ points: [], totals: { calls: 0, cost_usd: 0 } });
            }
        };
        load();
        const t = setInterval(load, 60_000); // refresh every 60s
        return () => { cancelled = true; clearInterval(t); };
    }, [isPublished, flow?.id]);

    return (
        <div className={`border rounded-md p-4 space-y-3 bg-[#0a0a0a] ${isPublished ? env.bg : "border-white/10"}`}
            data-testid={`pub-card-${env.key}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <div>
                        <div className={`text-sm font-medium ${env.color}`}>{env.label}</div>
                        <div className="font-mono text-[10px] text-white/40">{env.key}</div>
                    </div>
                </div>
                {isPublished ? (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-emerald-400/30 text-emerald-400"
                        data-testid={`pub-card-${env.key}-live-pill`} aria-label={`live in ${env.label}`}>
                        live
                    </span>
                ) : (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-white/15 text-white/40"
                        aria-label={`not published in ${env.label}`}>
                        not published
                    </span>
                )}
            </div>
            {isPublished ? (
                <>
                    <div className="space-y-1.5 text-xs">
                        <KV label="Pinned version" value={`v${pub.pinned_version_no}`} />
                        <KV label="Rate limit" value={`${pub.rate_limit_per_minute}/min`} />
                        <KV label="API key required" value={pub.require_api_key ? "Yes" : "No"} />
                        {pub.webhook_url && <KV label="Webhook" value={truncate(pub.webhook_url, 24)} />}
                        <KV label="Calls (all time)" value={pub.call_count || 0} />
                    </div>
                    <div className="border-t border-white/10 pt-3" data-testid={`pub-spark-${env.key}`}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="label-xs flex items-center gap-1.5">
                                <TrendingUp className="w-3 h-3 text-[#7C3AED]" /> Last 24h
                            </div>
                            {spark && (
                                <div className="font-mono text-[10px] text-white/60">
                                    {spark.totals.calls} calls · ${spark.totals.cost_usd.toFixed(5)}
                                </div>
                            )}
                        </div>
                        {spark ? (
                            <Sparkline points={spark.points} metric="cost" color="#7C3AED" height={28} showAxis={false} />
                        ) : (
                            <div className="h-7 animate-pulse bg-white/5 rounded" />
                        )}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <button onClick={onEdit} data-testid={`pub-edit-${env.key}`}
                            className="flex-1 px-2 py-1.5 border border-white/15 rounded-md text-[11px] hover:bg-white/5">
                            Edit
                        </button>
                        <button onClick={onUnpublish} data-testid={`pub-unpublish-${env.key}`}
                            className="px-2 py-1.5 bg-red-400/10 border border-red-400/30 text-red-400 rounded-md text-[11px] hover:bg-red-400/20 flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Unpublish
                        </button>
                    </div>
                </>
            ) : (
                <button onClick={onPublish} data-testid={`pub-publish-${env.key}`}
                    className="w-full px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] flex items-center justify-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Publish to {env.label.toLowerCase()}
                </button>
            )}
        </div>
    );
}

function PublishedDocs({ pubs, flow }) {
    const [activeEnv, setActiveEnv] = useState(pubs[0]?.environment);
    const pub = pubs.find((p) => p.environment === activeEnv);
    const baseUrl = window.location.origin;
    const endpoint = `${baseUrl}/api/v1/flows/${flow.slug}/${pub?.environment}`;

    const curlExample = `curl -X POST '${endpoint}' \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({
        input: sampleInput(flow.input_schema),
        metadata: { clientId: "<your-client-id>", siteId: "<site-id>", studyId: "<study-id>" },
    }, null, 2)}'`;

    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 space-y-3" data-testid="pub-docs">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-[#7C3AED]" />
                    <div className="font-display text-sm tracking-tight">Integration docs</div>
                </div>
                <div className="flex gap-1">
                    {pubs.map((p) => (
                        <button key={p.environment} onClick={() => setActiveEnv(p.environment)}
                            data-testid={`pub-docs-tab-${p.environment}`}
                            className={`px-2.5 py-1 text-[11px] font-mono rounded-md ${activeEnv === p.environment ? "bg-[#7C3AED] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>
                            {p.environment}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-3">
                <div>
                    <div className="label-xs mb-1">Endpoint</div>
                    <div className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-md px-3 py-2 font-mono text-[11px]">
                        <span className="text-emerald-400">POST</span>
                        <span className="text-white/80 flex-1 truncate">{endpoint}</span>
                        <button onClick={() => { navigator.clipboard.writeText(endpoint); toast.success("Copied"); }}
                            className="text-white/40 hover:text-white">
                            <Copy className="w-3 h-3" />
                        </button>
                    </div>
                </div>
                <div>
                    <div className="label-xs mb-1">cURL example</div>
                    <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[10px] font-mono whitespace-pre-wrap break-all max-h-72 overflow-auto" data-testid="pub-curl">
{curlExample}
                    </pre>
                    <button onClick={() => { navigator.clipboard.writeText(curlExample); toast.success("Copied"); }}
                        className="mt-1 text-[10px] text-white/50 hover:text-white flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copy
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <SchemaBlock title="Input schema" schema={flow.input_schema} />
                    <SchemaBlock title="Output schema" schema={flow.output_schema} />
                </div>
                <div>
                    <div className="label-xs mb-1">Integration contract response keys</div>
                    <div className="flex flex-wrap gap-1">
                        {["request_id", "flow_run_id", "status", "result", "error", "usage", "costs", "executionMeta"].map((k) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-[#050505] border border-white/10 font-mono text-white/70">
                                {k}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PublishModal({ flow, environment, existing, onClose, onSaved }) {
    const [pinnedVersion, setPinnedVersion] = useState(existing?.pinned_version_no || flow.active_version_no);
    const [rateLimit, setRateLimit] = useState(existing?.rate_limit_per_minute || 60);
    const [requireApiKey, setRequireApiKey] = useState(existing?.require_api_key !== false);
    const [webhookUrl, setWebhookUrl] = useState(existing?.webhook_url || "");
    const [description, setDescription] = useState(existing?.description || "");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/admin/flows/${flow.id}/publish`, {
                environment,
                pinned_version_no: Number(pinnedVersion),
                rate_limit_per_minute: Number(rateLimit),
                require_api_key: requireApiKey,
                webhook_url: webhookUrl.trim() || null,
                description: description.trim() || null,
                is_active: true,
            });
            toast.success(existing ? `Updated ${environment} publication` : `Published to ${environment}`);
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="pub-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <Rocket className="w-4 h-4 text-[#7C3AED] mr-2" />
                    <div className="font-display text-lg">{existing ? "Edit" : "Publish"} {environment}</div>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <div className="label-xs mb-1.5">Pinned version</div>
                        <input type="number" min="1" max={flow.version_count || 1}
                            value={pinnedVersion} onChange={(e) => setPinnedVersion(e.target.value)}
                            data-testid="pub-pinned-version"
                            className="input text-xs font-mono" />
                        <div className="text-[10px] text-white/40 mt-1">Latest active = v{flow.active_version_no} (v1–v{flow.version_count})</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-xs mb-1.5">Rate limit / min</div>
                            <input type="number" min="1" value={rateLimit} onChange={(e) => setRateLimit(e.target.value)}
                                data-testid="pub-rate-limit" className="input text-xs font-mono" />
                        </div>
                        <div>
                            <div className="label-xs mb-1.5">API key required</div>
                            <select value={requireApiKey ? "yes" : "no"} onChange={(e) => setRequireApiKey(e.target.value === "yes")}
                                data-testid="pub-require-key" className="input text-xs">
                                <option value="yes">Yes</option>
                                <option value="no">No (public)</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Webhook callback URL (optional)</div>
                        <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://your-saas.example.com/sitecentric/webhook"
                            data-testid="pub-webhook" className="input text-xs font-mono" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Public description (shown in /api/v1/flows/{flow.slug} discovery)</div>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                            className="input text-xs" />
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                        <button type="submit" disabled={busy} data-testid="pub-submit"
                            className="flex-1 bg-[#7C3AED] text-white py-2 rounded-md text-xs font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                            {busy ? "Saving…" : existing ? "Update publication" : `Publish to ${environment}`}
                        </button>
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function KV({ label, value }) {
    return (
        <div className="flex items-baseline gap-2">
            <div className="text-[10px] text-white/40 uppercase tracking-wider w-24 shrink-0">{label}</div>
            <div className="font-mono text-[11px] text-white/80 truncate">{value}</div>
        </div>
    );
}

function SchemaBlock({ title, schema }) {
    return (
        <div>
            <div className="label-xs mb-1">{title}</div>
            <pre className="bg-[#050505] border border-white/10 rounded-md p-2 text-[10px] font-mono whitespace-pre-wrap max-h-32 overflow-auto">
                {JSON.stringify(schema || {}, null, 2)}
            </pre>
        </div>
    );
}

function truncate(s, n) {
    if (!s) return "—";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function sampleInput(schema) {
    if (!schema || schema.type !== "object" || !schema.properties) return {};
    const out = {};
    for (const [k, v] of Object.entries(schema.properties)) {
        if (v.example !== undefined) out[k] = v.example;
        else if (v.type === "string") out[k] = "string";
        else if (v.type === "number" || v.type === "integer") out[k] = 0;
        else if (v.type === "boolean") out[k] = false;
        else if (v.type === "array") out[k] = [];
        else if (v.type === "object") out[k] = {};
    }
    return out;
}
